import Vue from 'vue';

// Threshold now in Vuex: state.profileActivity.thresholdMilliseconds

// Check if route is a patient profile route
const isPatientProfileRoute = (route) => {
  return route && route.path && route.path.includes('/site/') && route.path.includes('/patient/');
};

let autoLogIntervalId = null; // Keep track of the interval ID

export default ({ app, store }) => {
  console.log('[ProfileActivity] Plugin initializing...');

  // Prevent duplicate initialization
  let currentTrackingProfileId = null; // Current tracked profile ID for this plugin instance

  // --- Helper Functions ---

  const startAutoLogging = () => {
    if (autoLogIntervalId) {
      clearInterval(autoLogIntervalId);
    }
    autoLogIntervalId = setInterval(() => {
      store.dispatch('profileActivity/autoLogPeriodic');
    }, store.state.profileActivity.autoLogIntervalMilliseconds);
    console.log(`[ProfileActivity] Auto-logging started with interval ID: ${autoLogIntervalId}`);
  };

  const stopAutoLogging = (finalLog = false) => {
    if (autoLogIntervalId) {
      clearInterval(autoLogIntervalId);
      autoLogIntervalId = null;
      console.log('[ProfileActivity] Auto-logging stopped.');
    }
    if (finalLog && store.getters['profileActivity/isTracking']) {
      // Dispatch a final log when stopping, if specified
      console.log('[ProfileActivity] Dispatching final log on stopAutoLogging.');
      // Use a different note for this final auto-log if needed, or just let logActivity handle it
      store.dispatch('profileActivity/logActivity', { isFinalLog: true, isAutoLog: true, skipNoteUpdate: true });
    }
  };

  const startTrackingProfile = (route) => {
    console.log(`[ProfileActivity] Attempting startTrackingProfile for route: ${route ? route.path : 'undefined'}`);
    if (!route || !route.params) {
      console.log('[ProfileActivity] startTrackingProfile: Invalid route object.');
      return;
    }
    
    const siteId = route.params.slug;
    const siteProfileIdParam = route.params.site_profile_id;
    console.log(`[ProfileActivity] startTrackingProfile: Target site=${siteId}, site_profile_id=${siteProfileIdParam}`);

    // If already tracking this profile in the store, and currentTrackingProfileId matches, do nothing.
    if (store.getters['profileActivity/isTracking'] && 
        store.state.profileActivity.siteProfileId === siteProfileIdParam && 
        currentTrackingProfileId === siteProfileIdParam) {
        console.log(`[ProfileActivity] startTrackingProfile: Already actively tracking profile ${siteProfileIdParam}. Ensuring plugin's currentTrackingProfileId is set.`);
        currentTrackingProfileId = siteProfileIdParam; // Ensure it's set
        return;
    }
    
    let watcherTriggeredAndMatched = false;

    console.log('[ProfileActivity] startTrackingProfile: Setting up profile data watcher.');
    
    const unwatch = store.watch(
      (state) => ({
        loaded: store.getters['profile/siteProfileLoaded'],
        profileId: store.getters['profile/profileId'], // This is the main profile ID (e.g. UUID)
        loadedSiteProfileId: store.state.profile.siteProfile?.id // This is the site-specific ID (e.g. an integer)
      }),
      (newState, oldState) => {
        // We need to match against the site_profile_id from the route params, which corresponds to store.state.profile.siteProfile.id
        console.log(`[ProfileActivity] Watcher triggered. State: loaded=${newState.loaded}, mainProfileId=${newState.profileId}, loadedSiteProfileId=${newState.loadedSiteProfileId}`);

        if (newState.loaded && newState.profileId && newState.loadedSiteProfileId && String(newState.loadedSiteProfileId) === String(siteProfileIdParam)) {
          // Final permission check: ensure user has permission now that all data is loaded.
          if (!store.getters['portal/canTrackTime']) {
            console.log('[ProfileActivity] Watcher: User does not have permission for time tracking. Aborting.');
            unwatch();
            return;
          }
           console.log(`[ProfileActivity] Watcher: Profile loaded and matches target route (${siteProfileIdParam}). Dispatching startTracking with mainProfileId ${newState.profileId}.`);
           store.dispatch('profileActivity/startTracking', {
             profileId: newState.profileId, // Use the main profile ID for tracking
             siteId: siteId,
             siteProfileId: siteProfileIdParam // Pass siteProfileId for matching with route parameters
           }).then(() => {
              startAutoLogging(); // Start auto-logging once tracking is confirmed
           });
           currentTrackingProfileId = siteProfileIdParam; // Track by siteProfileIdParam as it's directly from the route
           watcherTriggeredAndMatched = true;
           console.log('[ProfileActivity] Watcher: Calling unwatch().');
           unwatch();
        } else {
            console.log(`[ProfileActivity] Watcher: Conditions not met (loaded=${newState.loaded}, mainProfileId=${newState.profileId}, loadedSiteProfileId=${newState.loadedSiteProfileId} vs target site_profile_id=${siteProfileIdParam})`);
        }
      },
      {
          immediate: true,
          deep: false
      }
    );

    // Stop watching after 10s if profile not matched
    const timeoutId = setTimeout(() => {
      if (!watcherTriggeredAndMatched) { 
        console.log('[ProfileActivity] Watcher timeout: Correct profile did not load/match target within 10s. Calling unwatch().');
        unwatch();
      }
    }, 30000);
  };

  const showActivityToast = async (to, from) => {
    console.log(`[ProfileActivity] showActivityToast called for navigation from: ${from.path}`);
    const totalMs = store.getters['profileActivity/activeTimeSpentMs'];
          
    // Attempt a final log before showing the toast.
    // This ensures the very last bit of time is captured.
    // The logActivity action itself will reset tracking if it's a final log.
    const loggedId = await store.dispatch('profileActivity/logActivity', { isFinalLog: true, isAutoLog: true, skipNoteUpdate: true });
    stopAutoLogging(false); // Stop auto-logging, final log already attempted
    currentTrackingProfileId = null; // Clear plugin's tracked profile as we are leaving

    if (loggedId && totalMs > 1000) { // Only show toast for logs over 1 second
        const minutesLogged = Math.floor(totalMs / 60000);
        const secondsLogged = Math.round((totalMs % 60000) / 1000);
        
        let durationMessage;
        if (minutesLogged > 0) {
            durationMessage = `${minutesLogged} minute(s)`;
            if (secondsLogged > 0) {
                durationMessage += ` and ${secondsLogged} second(s)`;
            }
        } else {
            durationMessage = `${secondsLogged} second(s)`;
        }

        app.router.app.$toast.add({
            severity: 'info',
            summary: 'Activity Logged',
            detail: `Logged ${durationMessage} for patient. ` +
                    (loggedId ? `<a href="/communication-log/${loggedId}" target="_blank">Review/Edit Log</a>` : 'Review not available'),
            life: 6000, // Keep toast for 6 seconds
            escape: false // Important to allow HTML in detail if your toast supports it
        });
        console.log(`[ProfileActivity] Activity toast shown for ${durationMessage}. Log ID: ${loggedId}`);
    } else if (totalMs <= 1000) {
        console.log('[ProfileActivity] No significant time to log, toast skipped.');
    } else if (!loggedId) {
        console.warn('[ProfileActivity] Activity was likely logged, but no log ID returned for toast link.');
        // Optionally, show a simpler toast without the link
         app.router.app.$toast.add({
            severity: 'info',
            summary: 'Activity Logged',
            detail: 'Activity logged for patient. Review link unavailable.',
            life: 6000
        });
    }
  };

  // --- Global Method for Manual Checks --- (This might be less relevant with auto-logging)
  Vue.prototype.$navigateWithActivityCheck = async (to) => {
    console.log(`[ProfileActivity] $navigateWithActivityCheck called for target: ${typeof to === 'string' ? to : to.path}`);
    const isCurrentlyTracking = store.getters['profileActivity/isTracking'];
    const trackedProfileId = store.state.profileActivity.siteProfileId;
    
    if (isCurrentlyTracking && currentTrackingProfileId && trackedProfileId === currentTrackingProfileId) {
      const isToString = typeof to === 'string';
      const toPath = isToString ? to : to.path;
      
      // Check if destination is still within the same patient profile using currentTrackingProfileId (which is site_profile_id)
      const isSamePatientProfile = toPath.includes(`/patient/${currentTrackingProfileId}/`);
      
      if (isSamePatientProfile) {
        console.log(`[ProfileActivity] $navigateWithActivityCheck: Navigation within same patient profile ${currentTrackingProfileId}. Continuing without dialog.`);
        // app.router.push(to); // Proceed with navigation
        return true; // Allow navigation
      }
    }
    
    // If navigating away from a tracked profile, show toast
    if (isCurrentlyTracking && currentTrackingProfileId && store.getters['profileActivity/shouldShowActivityToast']) {
      console.log('[ProfileActivity] $navigateWithActivityCheck: Leaving tracked profile, conditions met for toast.');
      // showActivityToast needs 'from' route. We don't have it directly here. 
      // This method might need rethinking or removal if beforeEach handles all cases.
      // For now, let's assume it won't show toast but just allows/disallows navigation
      // The beforeEach guard is more robust for handling toasts on navigation.
      
      // Perform final log before allowing navigation away
      await store.dispatch('profileActivity/logActivity', { isFinalLog: true, isAutoLog: true, skipNoteUpdate: true });
      stopAutoLogging(false); // Final log done in line above
      currentTrackingProfileId = null;
      // app.router.push(to); // Proceed with navigation
      return true; // Allow navigation
    } else {
      console.log('[ProfileActivity] $navigateWithActivityCheck: Conditions not met for toast, or not tracking. Resetting if needed.');
      if (isCurrentlyTracking) {
        // If it was tracking but no toast, still do a final log and reset
        await store.dispatch('profileActivity/logActivity', { isFinalLog: true, isAutoLog: true, skipNoteUpdate: true });
        stopAutoLogging(false);
      }
      currentTrackingProfileId = null;
      // app.router.push(to); // Proceed with navigation
      return true;
    }
  };

  // --- Router Guards ---
  app.router.beforeEach(async (to, from, next) => {
    console.log(`[ProfileActivity] beforeEach: Navigating FROM ${from.path} (currentTracked: ${currentTrackingProfileId}) TO ${to.path}`);
    
    const leavingPatientProfile = isPatientProfileRoute(from);
    const enteringPatientProfile = isPatientProfileRoute(to);
    const fromSiteProfileId = from.params.site_profile_id;
    const toSiteProfileId = to.params.site_profile_id;
    
    // Check if this is navigation within the same patient profile
    let isWithinSamePatientProfile = enteringPatientProfile && leavingPatientProfile && 
                                      toSiteProfileId && fromSiteProfileId && // Ensure both IDs are present
                                      toSiteProfileId === fromSiteProfileId;

    if (from.path === to.path) {
      isWithinSamePatientProfile = true;
    }
    
    // If we're navigating within the same patient profile, don't trigger logout dialogs
    if (isWithinSamePatientProfile) {
      console.log(`[ProfileActivity] beforeEach: Navigation within same patient profile ${fromSiteProfileId}. Continuing without dialog.`);
      next();
      return;
    }

    // Check if leaving the currently tracked patient profile
    // This implies currentTrackingProfileId must match from.params.site_profile_id
    if (leavingPatientProfile && fromSiteProfileId === currentTrackingProfileId) {
       console.log(`[ProfileActivity] beforeEach: Leaving currently tracked profile page ${from.path} (ID: ${currentTrackingProfileId}).`);
       
       const shouldShowToast = store.getters['profileActivity/shouldShowActivityToast'];

       if (shouldShowToast) {
         console.log('[ProfileActivity] beforeEach: Threshold met for leaving profile, showing toast.');
         await showActivityToast(to, from); // This will handle final logging and resetting
         // No need to call stopAutoLogging here, showActivityToast handles it.
         // currentTrackingProfileId is also reset by showActivityToast.
       } else {
         console.log('[ProfileActivity] beforeEach: Threshold not met or no time to log, resetting tracking in store if it was active.');
         if (store.getters['profileActivity/isTracking']) {
            await store.dispatch('profileActivity/logActivity', { isFinalLog: true, isAutoLog: true, skipNoteUpdate: true });
         }
         stopAutoLogging(false); // Stop auto-logging, final log already attempted or not needed
         currentTrackingProfileId = null; // Clear plugin's tracked profile
       }
    }
    // If not leaving a tracked profile, or conditions not met, proceed
    next();
  });

  app.router.afterEach((to, from) => {
     console.log(`[ProfileActivity] afterEach: Navigated FROM ${from.path} TO ${to.path}. Current plugin tracked ID: ${currentTrackingProfileId}`);
     
     const enteringPatientProfile = isPatientProfileRoute(to);
     const leavingPatientProfile = isPatientProfileRoute(from);
     const toSiteProfileId = to.params.site_profile_id;
     const fromSiteProfileId = from.params.site_profile_id;
     
     // Check if this is sub-navigation within the same patient profile
     let isWithinSamePatientProfile = enteringPatientProfile && leavingPatientProfile && 
                                        toSiteProfileId && fromSiteProfileId && // Ensure both IDs are present
                                        toSiteProfileId === fromSiteProfileId;
     
     if (from.path === to.path) {
      isWithinSamePatientProfile = true;
    }

     if (isWithinSamePatientProfile) {
       console.log(`[ProfileActivity] afterEach: Navigation within same patient profile ${toSiteProfileId}. Preserving tracking state.`);
       // Simply ensure the currentTrackingProfileId is aligned with the current route
       if (toSiteProfileId !== currentTrackingProfileId && store.getters['profileActivity/isTracking']) {
         currentTrackingProfileId = toSiteProfileId;
       }
       // No need to reset or restart tracking as we're still on the same patient
       return;
     }

     // Scenario 1: Entering a patient profile route (from non-patient page or different patient)
     if (enteringPatientProfile && (!leavingPatientProfile || toSiteProfileId !== fromSiteProfileId)) {
        console.log(`[ProfileActivity] afterEach: Entered profile page ${to.path} (Target site_profile_id: ${toSiteProfileId}).`);

        // If the store is already tracking THIS profileId, and plugin agrees, ensure visibility/resume.
        if (store.getters['profileActivity/isTracking'] && store.state.profileActivity.siteProfileId === toSiteProfileId) {
            console.log(`[ProfileActivity] afterEach: Store is already tracking target profile ${toSiteProfileId}. Ensuring plugin state and visibility.`);
            currentTrackingProfileId = toSiteProfileId; // Align plugin's tracker
            if (document.visibilityState === 'visible' && !store.state.profileActivity.isTabActive) { // isTabActive reflects tab visibility tracking
                console.log('[ProfileActivity] afterEach: Tab is visible and store tracking was paused, resuming.');
                store.dispatch('profileActivity/resumeTracking');
            }
            return; // Don't restart tracking if already tracking the correct profile
        } 
        // If store is NOT tracking this profile, or tracking a different one, or plugin's currentTrackingProfileId is out of sync
        else {
            console.log(`[ProfileActivity] afterEach: Store not tracking target profile ${toSiteProfileId} (or tracking different: ${store.state.profileActivity.siteProfileId}). Resetting and starting new tracking.`);
            // If it was tracking something else, ensure it's reset.
            if (store.getters['profileActivity/isTracking'] && store.state.profileActivity.siteProfileId !== toSiteProfileId) {
                console.log(`[ProfileActivity] afterEach: Resetting previous tracking for ${store.state.profileActivity.siteProfileId}.`);
                // Before resetting, ensure previous activity is logged and auto-logging stopped
                stopAutoLogging(true); // true to attempt a final log for the previous profile
                // The resetTracking action is now part of logActivity when isFinalLog is true.
                // store.dispatch('profileActivity/resetTracking'); // This might be redundant if logActivity handles it.
            }
            currentTrackingProfileId = null; // Signal startTrackingProfile to fully initialize
            startTrackingProfile(to); // This will set currentTrackingProfileId and startAutoLogging upon successful watcher match & dispatch
        }
     } 
     // Scenario 2: Leaving a patient profile to a non-profile page
     // Handled by beforeEach for dialog, but if no dialog, reset has already happened.
     // If we land here and we were on a profile, currentTrackingProfileId should be null from beforeEach or dialog.
     else if (leavingPatientProfile && !enteringPatientProfile) {
        console.log(`[ProfileActivity] afterEach: Left profile page ${from.path} to non-profile page ${to.path}.`);
        // If currentTrackingProfileId is still set here, it means beforeEach didn't clear it (e.g. no dialog and no reset path taken).
        // This typically shouldn't happen if beforeEach logic is correct.
        if (currentTrackingProfileId) {
             console.warn(`[ProfileActivity] afterEach: currentTrackingProfileId (${currentTrackingProfileId}) was still set after leaving a profile. Clearing and stopping logs.`);
             stopAutoLogging(true); // Attempt final log
             currentTrackingProfileId = null;
        }
        // Ensure tracking is stopped if store somehow still thinks it is
        if (store.getters['profileActivity/isTracking'] && store.state.profileActivity.siteProfileId === fromSiteProfileId) {
            console.warn(`[ProfileActivity] afterEach: Store was still tracking ${fromSiteProfileId} after leaving. Resetting and stopping logs.`);
            stopAutoLogging(true); // Attempt final log
            // store.dispatch('profileActivity/resetTracking'); // logActivity with isFinalLog will handle this
        }
     }
  });

  // --- Client-Side Initialization & Unload Handling ---
  if (process.client) {
    app.router.onReady(() => {
      console.log('[ProfileActivity] Router is ready.');
      
      // Add visibility change event listener
      document.addEventListener('visibilitychange', () => {
        console.log(`[ProfileActivity] Visibility changed to: ${document.visibilityState}`);
        
        // Only act if we're currently tracking a profile (based on plugin's view)
        // AND the store also confirms it's tracking that same profile
        if (currentTrackingProfileId && store.getters['profileActivity/isTracking'] && 
            store.state.profileActivity.siteProfileId === currentTrackingProfileId) {
          
          store.dispatch('profileActivity/updateTimestamp'); // Always update timestamp first
          if (document.visibilityState === 'visible') {
            console.log(`[ProfileActivity] Tab became visible for ${currentTrackingProfileId}, resuming tracking via store.`);
            store.dispatch('profileActivity/resumeTracking');
            // Consider if auto-logging should (re)start or if its interval handles pauses implicitly
            // If interval continues running but dispatches do nothing when tab is inactive, this is fine.
            // For safety, we can ensure it's running if not already.
            if (!autoLogIntervalId) {
                console.log('[ProfileActivity] Restarting auto-logger on tab visibility.');
                startAutoLogging();
            }
          } else {
            console.log(`[ProfileActivity] Tab became hidden for ${currentTrackingProfileId}, pausing tracking via store.`);
            store.dispatch('profileActivity/pauseTracking');
            // Optionally pause auto-logging explicitly, though autoLogPeriodic checks isTabActive
            // clearInterval(autoLogIntervalId); 
            // autoLogIntervalId = null;
            // console.log('[ProfileActivity] Auto-logging paused due to tab hidden.');
          }
        } else {
          console.log(`[ProfileActivity] Visibility changed, but not actively tracking profile ${currentTrackingProfileId} or store mismatch (store tracking: ${store.getters['profileActivity/isTracking']}, store profile: ${store.state.profileActivity.siteProfileId}). No action.`);
        }
      });
    });

    // Handle browser close/reload
    window.addEventListener('beforeunload', async (event) => {
      console.log('[ProfileActivity] beforeunload event triggered.');
      
      // Check if we are currently on a patient profile page and tracking that specific profile
      const currentRoute = app.router.currentRoute;
      if (isPatientProfileRoute(currentRoute) && 
          currentRoute.params.site_profile_id === currentTrackingProfileId && 
          store.getters['profileActivity/isTracking'] &&
          store.state.profileActivity.siteProfileId === currentTrackingProfileId) {

        // Attempt to log final activity synchronously if possible, or at least mark for logging.
        // Browsers are very restrictive with async operations in `beforeunload`.
        console.log('[ProfileActivity] beforeunload: Attempting final log for currently tracked profile.');
        // Log final activity. isFinalLog = true will reset tracking in the store.
        // This is a best-effort. If it fails, time might be lost.
        await store.dispatch('profileActivity/logActivity', { isFinalLog: true, isAutoLog: true, skipNoteUpdate: true });
        stopAutoLogging(false); // Stop any further auto-logging attempts

        const shouldShowPrompt = store.getters['profileActivity/shouldShowActivityToast']; // Using this to decide if a prompt is even relevant
        if (shouldShowPrompt) { // This getter uses thresholdMilliseconds, which is 10s currently
          console.log('[ProfileActivity] beforeunload: Threshold met, prompting user (browser native). Time has been attempted to be logged.');
          // Standard way to trigger prompt. The actual logging was attempted above.
          // The prompt itself is generic and doesn't guarantee the user stays.
          event.preventDefault(); 
          event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'; // Generic message
          return event.returnValue; 
        }
        console.log('[ProfileActivity] beforeunload: Threshold not met or time logged. Allowing unload without native prompt.');
      } else {
        console.log('[ProfileActivity] beforeunload: Not on a tracked patient profile page or tracking inactive. Allowing unload.');
      }
    });
  }
}; 